import {
  billableCalculationLabel, billableCalculationTotals, billableHoursTotal, billingNumber, formatBillingDuration,
  formatBillingNumber, groupBillableCalculationSteps, summarizeBillableCalculation,
} from "./billingCalculation.js";
import { billableServiceContext } from "./billingPrestationDetails.js";

const ROUTE_KINDS = ["loading", "delivery", "dompeur"];

// Every displayed duration is rounded to the minute; pages say so once instead of « ≈ » everywhere.
export const timeLabel = (seconds) => seconds == null ? "—" : formatBillingDuration(seconds).replace("≈ ", "");
export const kmLabel = (km, digits = 0) => km == null ? "" : `${formatBillingNumber(km, digits)} km`;
export const tonsLabel = (tons) => tons == null ? "" : `${formatBillingNumber(tons, 3)} t`;
// Here « ≈ » marks only a time estimated for lack of an arrival time.
export const approx = (estimated) => estimated ? "≈ " : "";
export const estimatedLabel = (seconds) => seconds ? `dont ≈ ${timeLabel(seconds)} estimées` : "";
const sum =(values) => values.reduce((total, value) => total + value, 0);
// A total is known only when every part is known.
const knownSum = (values) => values.some((value) => value === null) ? null : sum(values);

function coefficient(item) {
  const numerator = billingNumber(item.calculation_details?.coefficient?.numerator);
  const denominator = billingNumber(item.calculation_details?.coefficient?.denominator);
  return numerator === null || denominator === null || denominator <= 0 ? null : { numerator, denominator };
}

function stepTons(step) {
  if (step.kind === "dompeur") return knownSum(step.items.map((item) => billingNumber(item.quantity)));
  // Loading shows the tons delivered from it, as corrected in Consolidation.
  if (step.kind === "loading") return step.context.delivered_tons;
  if (step.kind !== "delivery") return null;
  // A delivery coefficient is the order's delivered tons over the tons delivered at the whole stop.
  return knownSum(step.items.map((item) => coefficient(item)?.numerator ?? null));
}

function siteName(step) {
  const { destination, customer_name: customer } = step.context;
  return destination.map((location) => location.name).filter(Boolean).join(" / ")
    || customer || step.items.find((item) => item.order_reference)?.order_reference
    || { loading: "Meunerie", delivery: "Livraison" }[step.kind] || billableCalculationLabel(step.items[0]);
}

function truckNumber(items) {
  const crew = items.find((item) => item.service_crew?.truck_number)?.service_crew?.truck_number;
  if (crew) return crew;
  const description = items.find((item) => /Camion /.test(item.source_description || ""))?.source_description || "";
  return /Camion ([^·]+)/.exec(description)?.[1]?.trim() || null;
}

/** Attributed time of a stop split between the road and the site, in the saved proportions. */
function stepTime(step) {
  const hours = billableHoursTotal(step.items);
  const seconds = hours === undefined || hours === null ? null : hours * 3600;
  let travel = billingNumber(step.block?.travel_seconds);
  let onsite = billingNumber(step.block?.onsite_seconds);
  const net = billingNumber(step.block?.net_seconds);
  // Sans heure d'arrivée : le temps d'arrêt estimé par le serveur, plafonné à l'arrêt ; le reste est de la route.
  const estimate = travel === null && onsite === null && net ? step.context.estimated_onsite_seconds : null;
  if (estimate !== null) { onsite = Math.min(estimate, net); travel = net - onsite; }
  if (seconds === null || travel === null || onsite === null || travel + onsite <= 0) return { seconds, travel: null, onsite: null, km: null, estimated: false };
  const share = net ? Math.min(seconds / net, 1) : null;
  return { seconds, travel: seconds * travel / (travel + onsite), onsite: seconds * onsite / (travel + onsite),
    km: share === null || step.context.route_km === null ? null : step.context.route_km * share, estimated: estimate !== null };
}

function buildSteps(items) {
  const steps = [];
  for (const item of items) {
    const kind = item.calculation_details?.kind || "";
    const block = item.calculation_details?.block;
    // Orders delivered at one stop share the same saved block.
    const key = ROUTE_KINDS.includes(kind) && block?.start ? `${kind}:${block.start}` : item.id;
    let step = steps.find((candidate) => candidate.key === key);
    if (!step) steps.push(step = { key, kind, block, context: billableServiceContext(item), items: [] });
    step.items.push(item);
  }
  return steps.map((step) => {
    const shared = coefficient(step.items[0]);
    return {
      ...step, ...stepTime(step), site: siteName(step), tons: stepTons(step),
      hours: billableHoursTotal(step.items), totals: billableCalculationTotals(step.items),
      // Part of the day's common time (inspection, return) attributed to this billing account.
      share: shared ? shared.numerator / shared.denominator : null,
      orders: step.items.filter((item) => item.order_reference),
    };
  });
}

/** Journées travaillées : les dates, comme le relevé de transport ; deux relevés de chauffeur le même jour font une journée. */
export const workedDayCount = (days) => new Set(days.map((day) => day.date).filter(Boolean)).size;

/** Chargement d'un Chargé PM saisi par son seul « Quitté à » : sa route vers la meunerie est comprise dans son temps de chargement. */
export const pmLoadingWithRoad = (step) => step.kind === "loading" && step.travel === null
  && step.items.some((item) => item.calculation_details?.pm_delivery_period);

/** Arrêts racontés dans l'ordre de la journée, et ce qu'on y a fait. */
export const STOP_KINDS = { loading: "Chargement", delivery: "Livraison", dompeur: "Dompeur", preload: "Préchargement" };
export const excludedSeconds = (block) => Math.max(0, (Number(block?.gross_seconds) || 0) - (Number(block?.net_seconds) || 0));

/** Lieu d'où part le camion (vide s'il est inconnu) ; un départ après un autre client (absent de `ownSites`, ou arrivé sans lieu) reste anonyme. */
export function stopDeparturePlace(step, ownSites = null) {
  const { origin, origin_kind: kind } = step.context;
  const named = !ownSites || kind !== "visit" || origin.every((location) => ownSites.has(location.name));
  return (named && origin.map((location) => location.name || location.address).filter(Boolean).join(" / "))
    || { visit: "après une livraison · autre client", day_start: "début de journée" }[kind] || "";
}

/** Part du temps commun du camion (inspection, retour) comptée à ce client : une ligne d'inspection par lieu livré s'additionne. */
export function commonShare(day) {
  const steps = day.steps.filter((step) => step.kind === "inspection" && step.share !== null);
  return steps.length ? steps.reduce((total, step) => total + step.share, 0) : null;
}

/** Un départ qui reprend exactement le lieu et l'heure de l'arrêt précédent n'apprend rien : il n'est pas répété. */
export const continuesFrom = (step, previous, place) => Boolean(previous?.block?.end) && step.block?.start === previous.block.end
  && (!place || place === previous.site);

const dayStart = (day) => day.steps.map((step) => step.block?.start || "").filter(Boolean).sort()[0] || "";

/** One entry per saved day sheet: who drove, where, and what it adds to the amount. */
export function groupBillableCalculationDays(items) {
  return groupBillableCalculationSteps(items).map((group) => {
    const crew = group.items.find((item) => item.service_crew)?.service_crew || {};
    return {
      key: group.key, date: group.date, steps: buildSteps(group.items), items: group.items,
      driver: group.items.find((item) => item.service_crew?.driver_name)?.service_crew.driver_name || null,
      truck: truckNumber(group.items), timezone: crew.timezone || null, period: crew.period === "evening" ? "evening" : "day",
      hours: billableHoursTotal(group.items), totals: billableCalculationTotals(group.items),
    };
  }).sort((a, b) => a.date.localeCompare(b.date) || dayStart(a).localeCompare(dayStart(b)) || (a.driver || "").localeCompare(b.driver || ""));
}

/** Days grouped by driver, each with its own time summary. */
export function groupBillableCalculationDrivers(days) {
  const drivers = new Map();
  for (const day of days) {
    const key = day.driver || "";
    if (!drivers.has(key)) drivers.set(key, { key, driver: day.driver, trucks: [], days: [] });
    const driver = drivers.get(key);
    driver.days.push(day);
    if (day.truck && !driver.trucks.includes(day.truck)) driver.trucks.push(day.truck);
  }
  return [...drivers.values()].map((driver) => ({ ...driver, summary: summarizeBillableTime(driver.days.flatMap((day) => day.items)) }));
}

/** The single hourly rate of the selection, or null when several apply. */
export function billableSingleHourlyRate(items) {
  const rates = summarizeBillableCalculation(items).filter((group) => group.unit === "heure");
  return rates.length === 1 && rates[0].rate !== null ? rates[0] : null;
}

function addLeg(bucket, step) {
  bucket.count += 1;
  bucket.seconds += step.travel;
  bucket.km = bucket.km === null || step.km === null ? null : bucket.km + step.km;
}

/** Hours read as loading at the mill + road + delivery on site + the shared inspection and return. */
export function summarizeBillableTime(items) {
  const days = groupBillableCalculationDays(items);
  const steps = days.flatMap((day) => day.steps);
  const loading = { seconds: 0, count: 0, tons: 0, estimated: 0, sites: new Map() };
  const delivery = { seconds: 0, count: 0, tons: 0, estimated: 0 };
  const toMills = { seconds: 0, km: 0, count: 0 };
  const toClients = { seconds: 0, km: 0, count: 0 };
  let inspection = 0, back = 0, other = 0, estimatedRoad = 0;
  for (const step of steps) {
    const split = step.travel !== null;
    if (step.estimated) estimatedRoad += step.travel;
    if (step.kind === "inspection") inspection += step.seconds ?? 0;
    else if (step.kind === "return") back += step.seconds ?? 0;
    else if (step.kind === "loading" || step.kind === "preload") {
      const site = loading.sites.get(step.site) || { site: step.site, seconds: 0, count: 0, tons: 0 };
      loading.sites.set(step.site, site);
      // Préchargement et Chargé PM sans heure d'arrivée : tout leur temps est du chargement, route comprise.
      const whole = !split && (step.kind === "preload" || pmLoadingWithRoad(step));
      const onsite = split ? step.onsite : whole ? step.seconds ?? 0 : 0;
      if (split) addLeg(toMills, step);
      else if (!whole) { other += step.seconds ?? 0; toMills.km = null; }
      if (step.estimated) loading.estimated += step.onsite;
      for (const target of [loading, site]) {
        target.seconds += onsite;
        target.count += 1;
        target.tons = target.tons === null || step.tons === null ? null : target.tons + step.tons;
      }
    } else if (step.kind === "delivery" || step.kind === "dompeur") {
      delivery.count += 1;
      delivery.tons = delivery.tons === null || step.tons === null ? null : delivery.tons + step.tons;
      if (split) { addLeg(toClients, step); delivery.seconds += step.onsite; } else { other += step.seconds ?? 0; toClients.km = null; }
      if (step.estimated) delivery.estimated += step.onsite;
    } else if (step.seconds !== null) other += step.seconds;
  }
  const hourly = items.filter((item) => billableHoursTotal([item]) !== undefined);
  const extras = items.filter((item) => !hourly.includes(item));
  return {
    days, loading: { ...loading, sites: [...loading.sites.values()] }, delivery,
    route: { seconds: toMills.seconds + toClients.seconds, count: toMills.count + toClients.count, estimated: estimatedRoad,
      km: toMills.km === null || toClients.km === null ? null : toMills.km + toClients.km, toMills, toClients },
    inspection, back, other, orderCount: new Set(items.map((item) => item.order_reference).filter(Boolean)).size,
    hours: billableHoursTotal(items), hourTotals: billableCalculationTotals(hourly), rate: billableSingleHourlyRate(items),
    // Tarif horaire inconnu (sans entreprise, tarif manquant) : « tarif à déterminer » plutôt que « plusieurs tarifs ».
    rateMissing: summarizeBillableCalculation(items).some((group) => group.unit === "heure" && group.rate === null),
    extras, extraTotals: billableCalculationTotals(extras), totals: billableCalculationTotals(items),
  };
}
